import { useState } from 'react';
import oktaApiClient from './oktaApiClient';
import './OktaLoginPage.css';
import sha256 from 'js-sha256';

const CODE_VERIFIER_KEY = 'ubiquity.okta_code_verifier';

function toBase64Url(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateCodeVerifier() {
  const random = new Uint8Array(32);
  window.crypto.getRandomValues(random);
  return toBase64Url(random);
}

/** PKCE S256: base64url(SHA-256(verifier)). Uses js-sha256 so it works on HTTP (crypto.subtle is unavailable outside HTTPS/localhost). */
function generateCodeChallenge(verifier) {
  const hashBytes = sha256.array(verifier);
  return toBase64Url(new Uint8Array(hashBytes));
}

function generateState() {
  const random = new Uint8Array(16);
  window.crypto.getRandomValues(random);
  return toBase64Url(random);
}

export default function OktaLoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await oktaApiClient.get('/auth/config');
      const { oktaDomain, clientId, redirectUri } = data || {};
      if (!oktaDomain || !clientId || !redirectUri) {
        setError('Auth is not configured.');
        setLoading(false);
        return;
      }

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = generateCodeChallenge(codeVerifier);
      const state = generateState();

      sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

      const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        scope: 'openid profile email',
        redirect_uri: redirectUri,
        state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });

      window.location.href = `https://${oktaDomain}/oauth2/v1/authorize?${params.toString()}`;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to start login';
      setError(message);
      setLoading(false);
    }
  };

  return (
    <div className="okta-login-page">
      <div className="okta-login-card">
        <div className="okta-login-header">Login</div>
        <div className="okta-login-body">
          <p className="okta-login-welcome">Sign in with Okta to continue.</p>
          {error ? <p className="okta-login-error">{error}</p> : null}
          <button
            type="button"
            className="okta-login-button"
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? 'Redirecting...' : 'Login with Okta'}
          </button>
        </div>
      </div>
    </div>
  );
}
